import type { ObservingPickerEntry } from './buildObservingPickerSections';

/**
 * picker entry 에 대응하는 sketching 룸을 삭제할 수 있는지 판단한다.
 *
 * 정책 (기존 `useSketchingRoomMutations.handleConfirmRemoveRoom` 와 동일하되,
 * 짐기록/영상 한 번이라도 저장된 방은 명시적으로 더 차단한다):
 *
 * 1. 삭제 API 가 `removeSketchingRoom(roomId)` 한 종류뿐이라, sketching 룸의
 *    `roomId` 가 식별 가능한 entry 만 후보다. 즉:
 *      - kind === 'sketch' 이며 entry.room.roomId 가 있다 → 후보
 *      - kind === 'additional' 이며 entry.linkedSketchRoom?.roomId 가 있다 → 후보
 *      - 그 외 (운반 경로 등 unlinked additional) → 항상 false
 * 2. 후보라도 다음 중 하나라도 채워져 있으면 삭제 불가:
 *      - 연결된 ObservingSession.status 가 REQUESTED 가 아님 (이미 시작됨)
 *      - entry.hasItems === true (짐 한 번이라도 저장)
 *      - entry.hasVideo === true (영상 한 번이라도 저장)
 *
 * 백엔드도 옵저빙 status === 'REQUESTED' 일 때만 삭제를 허용하므로, 위 조건은
 * UI 측 사전 차단 + 정책 명시 목적이다.
 */
export function canDeleteObservingRoom(entry: ObservingPickerEntry): boolean {
  const roomId = resolveRoomIdForDelete(entry);
  if (!roomId) return false;

  if (entry.hasItems || entry.hasVideo) return false;

  if (entry.kind === 'sketch') {
    const session = entry.session;
    if (session && session.status !== 'REQUESTED') return false;
    return true;
  }

  if (entry.linkedSketchRoom == null) return false;
  return entry.session.status === 'REQUESTED';
}

/**
 * picker entry → 삭제 API 에 넘길 sketching 룸의 roomId.
 * 삭제 가능한 후보가 아니면 null.
 */
export function resolveRoomIdForDelete(entry: ObservingPickerEntry): string | null {
  if (entry.kind === 'sketch') return entry.room.roomId ?? null;
  return entry.linkedSketchRoom?.roomId ?? null;
}

/**
 * 삭제 불가일 때 노출할 짧은 사유 (UI 툴팁/캡션용).
 *
 * - "기록을 시작했어요" — 짐/영상 중 하나라도 저장됨
 * - "삭제할 수 없어요" — 운반 경로 등 sketching 룸과 매칭되지 않는 추가공간
 *
 * 두 카테고리만 구분하면 사용자 입장에서 충분하다.
 */
export function describeDeleteBlockedReason(
  entry: ObservingPickerEntry
): '기록을 시작했어요' | '삭제할 수 없어요' | null {
  if (canDeleteObservingRoom(entry)) return null;
  if (entry.hasItems || entry.hasVideo) return '기록을 시작했어요';
  if (entry.kind === 'sketch') {
    const session = entry.session;
    if (session && session.status !== 'REQUESTED') return '기록을 시작했어요';
  } else {
    if (entry.session.status !== 'REQUESTED') return '기록을 시작했어요';
  }
  return '삭제할 수 없어요';
}
